import {
  IBudget,
  IProposal,
  IStory,
  IStoryContent,
  IAllProposalDetails
} from '@opengov/stories-shared/types';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import SvgIcon from '@mui/material/SvgIcon';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Close as CloseIcon } from '@opengov/react-capital-assets';
import { transport } from '@opengov/app-core/transport';
import { useQueryClient } from '@tanstack/react-query';

import React, { useEffect, useState } from 'react';

import { IModalMessages, ISelectedProposalInfo } from '../../../../types';
import { styles } from './ReplaceBudgetModal.styles';
import { IStoryBudget } from '../../AddProposalModal/AddProposalModal';
import { ReplaceBudgetProposalRow } from '../ReplaceBudgetProposalRow/ReplaceBudgetProposalRow';
import { useGetProposalsInBudget } from '../useGetProposalsInBudget';
import { getProposalDetails } from '../replaceBudgetProposalUtils';
import { MergeTagsOperation } from '../../../../stories/common/context/MergeTagsContext';
import {
  useReplaceBudgetInStory,
  useUpdateAllStoryContentsMergeTagsDB
} from '../../../../stories/components/StoryView/hooks';

export type IReplaceBudgetModalProps = {
  messages: IModalMessages;
  selectedStoryId: string;
  budget: IBudget;
  isTemplate: boolean;
  story: IStory;
  publicationId?: string;
  userId?: number;
  storyContents: IStoryContent[];
  proposalFieldsDetails: IAllProposalDetails;
  entityId: number;
  onClose(): void;
};

export const ReplaceBudgetModal: React.FC<IReplaceBudgetModalProps> = ({
  messages,
  budget,
  isTemplate,
  story,
  publicationId,
  userId,
  storyContents,
  entityId,
  selectedStoryId,
  proposalFieldsDetails,
  onClose
}) => {
  const queryClient = useQueryClient();
  const { mutateAsync: doReplaceBudget } = useReplaceBudgetInStory();
  const { mutateAsync: doUpdateMergeTags } = useUpdateAllStoryContentsMergeTagsDB();

  const [selectedBudget] = useState<IStoryBudget | null>(
    budget?.budgetTitle ? { id: budget.budgetId, title: budget.budgetTitle } : null
  );

  const [isReplacing, setIsReplacing] = useState<boolean>(false);
  const [replacingBudget, setReplacingBudget] = useState<IStoryBudget | null>(null);
  const [budgetsList, setBudgetsList] = useState<IStoryBudget[] | null>(null);
  const [isLoadingBudgets, setIsLoadingBudgets] = useState<boolean>(false);
  const [selectedReplacingProposal, setSelectedReplacingProposal] = useState<IProposal[]>([]);
  const [mapReplacingProposal, setMapReplacingProposal] = useState<{
    [fromProposalId: string]: { proposalId: number; title: string };
  }>({});

  const { isLoadingProposals, proposalsList: replacingProposalsList } = useGetProposalsInBudget(
    replacingBudget?.id ?? 0,
    null
  );

  useEffect(() => {
    if (!budgetsList) {
      setIsLoadingBudgets(true);
      void transport
        .url(`/api/v3/budgets?entity_id=${entityId}&limited_attributes=id,title`)
        .get()
        .json<{ budgets: IStoryBudget[] }>()
        .then((response) => {
          const allBudgets = (response?.budgets || []).map((budget) => ({
            ...budget,
            disabled: budget.id === selectedBudget?.id
          }));
          setBudgetsList(allBudgets);
        })
        .finally(() => setIsLoadingBudgets(false));
    }
  }, [budgetsList, entityId, selectedBudget?.id]);

  useEffect(() => {
    if (replacingProposalsList?.length && replacingBudget && budget) {
      const proposalDetails: ISelectedProposalInfo[] = getProposalDetails(budget, proposalFieldsDetails);
      const updatedSelectedReplacingProposal = [...selectedReplacingProposal];
      const updatedMapReplacingProposal = { ...mapReplacingProposal };
      replacingProposalsList?.forEach((proposal) => {
        const proposalObject = proposalDetails.find((prevProposal) => prevProposal.title === proposal.title);
        if (proposalObject) {
          const isProposalPresent = updatedSelectedReplacingProposal.find(
            (selectedProposal) => selectedProposal.id === proposal.id
          );
          !isProposalPresent && updatedSelectedReplacingProposal.push(proposal);
          updatedMapReplacingProposal[proposalObject.proposalId] = { title: proposal.title, proposalId: proposal.id };
          setMapReplacingProposal(updatedMapReplacingProposal);
          setSelectedReplacingProposal(updatedSelectedReplacingProposal);
        }
      });
    }
  }, [replacingProposalsList, replacingBudget]);

  const onChangeBudgetSelection = (budget: IStoryBudget): void => {
    setSelectedReplacingProposal([]);
    setMapReplacingProposal({});
    setReplacingBudget(budget);
  };

  const onChangeReplaceProposalSelection = (proposalSelected: IProposal, proposalId: number): void => {
    const updatedSelectedReplacingProposal = [...selectedReplacingProposal];
    const updatedMapReplacingProposal = { ...mapReplacingProposal };

    if (proposalSelected) {
      const isProposalPresent = updatedSelectedReplacingProposal.find(
        (proposal) => proposalSelected.id === proposal.id
      );
      !isProposalPresent && updatedSelectedReplacingProposal.push(proposalSelected);
      updatedMapReplacingProposal[proposalId] = { title: proposalSelected.title, proposalId: proposalSelected.id };
      setMapReplacingProposal(updatedMapReplacingProposal);
      setSelectedReplacingProposal(updatedSelectedReplacingProposal);
    } else {
      const proposalPresentIndex = updatedSelectedReplacingProposal.findIndex(
        (proposal) => mapReplacingProposal[proposalId].proposalId === proposal.id
      );
      if (proposalPresentIndex > -1) {
        updatedSelectedReplacingProposal.splice(proposalPresentIndex, 1);
        setSelectedReplacingProposal(updatedSelectedReplacingProposal);
      }
      delete updatedMapReplacingProposal[proposalId];
      setMapReplacingProposal(updatedMapReplacingProposal);
    }
  };

  const onChangeProposalSelection = (proposalSelected: IProposal) => {
    if (proposalSelected) setSelectedReplacingProposal([proposalSelected]);
    else setSelectedReplacingProposal([]);
  };

  const renderProposalDropdown = () => {
    return (
      <Box sx={styles.dropdownContainer}>
        <Typography id="replace-budget-proposals-label" variant="body2">{messages.proposals}</Typography>
        <Autocomplete
          options={replacingProposalsList ?? []}
          getOptionLabel={(option) => option.title}
          value={selectedReplacingProposal[0] ?? null}
          onChange={(_event, value) => onChangeProposalSelection(value)}
          size="small"
          disablePortal
          loading={isLoadingProposals}
          renderOption={(props, option) => <li {...props}>{option.title}</li>}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={messages.enterProposals}
              inputProps={{ ...params.inputProps, 'aria-labelledby': 'replace-budget-proposals-label', className: 'no-format' }}
            />
          )}
        />
      </Box>
    );
  };

  const renderBudgetDropdown = (): JSX.Element => {
    return (
      <Box sx={styles.dropdownContainer}>
        <Typography id="replace-budget-label" variant="body2">{messages.selectNewBudget}</Typography>
        <Autocomplete
          options={budgetsList ?? []}
          getOptionLabel={(option) => option.title}
          value={replacingBudget}
          onChange={(_event, value) => onChangeBudgetSelection(value)}
          size="small"
          disablePortal
          loading={isLoadingBudgets}
          getOptionDisabled={(option) => !!option.disabled}
          renderOption={(props, option) => <li {...props}>{option.title}</li>}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={messages.enterBudget}
              inputProps={{ ...params.inputProps, 'aria-labelledby': 'replace-budget-label', className: 'no-format' }}
            />
          )}
        />
      </Box>
    );
  };

  const replaceBudget = async (): Promise<void> => {
    setIsReplacing(true);
    try {
      const selectedProposalIds = selectedReplacingProposal.map((proposal) => proposal.id);
      const { mergeTags } = await doReplaceBudget({
        storyId: selectedStoryId,
        budgetId: replacingBudget!.id,
        proposalIds: selectedProposalIds,
        entityId
      });
      await doUpdateMergeTags({
        isTemplate,
        story,
        storyContents,
        publicationId: publicationId ?? '',
        userId: userId ?? 0,
        storyMergeTagsOperation: {
          operation: MergeTagsOperation.Replace,
          mergeTags,
          proposalsReplacementMap: mapReplacingProposal
        }
      });
      void queryClient.invalidateQueries({ queryKey: ['storyView', 'proposals'] });
    } finally {
      setIsReplacing(false);
      onClose();
    }
  };

  const checkIfLastProposalRemoved = () => {
    if (budget && budget.proposals?.length === 1) {
      const proposalId = budget.proposals[0].proposalId;
      if (!proposalFieldsDetails[proposalId]) {
        return true;
      }
    }
    return false;
  };

  const isLastProposalRemoved = checkIfLastProposalRemoved();
  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: styles.dialogPaper }} aria-labelledby="replace-budget-dialog-title">
      <DialogTitle id="replace-budget-dialog-title" sx={styles.dialogTitle}>
        {!selectedBudget ? messages.addBudget : messages.replaceBudget}
        <IconButton aria-label="close" onClick={onClose} size="small" sx={styles.closeButton}>
          <SvgIcon component={CloseIcon} inheritViewBox />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        <Box sx={styles.content}>
          <WarningAmberIcon sx={styles.warningIcon} aria-hidden="true" />
          <Box sx={styles.headerContent}>
            {selectedBudget ? <Typography variant="body2">{messages.replaceBudgetDesc}</Typography> : null}
            {renderBudgetDropdown()}
            {(!selectedBudget || isLastProposalRemoved) && renderProposalDropdown()}
            {replacingProposalsList?.length && selectedBudget && !isLastProposalRemoved ? (
              <Box sx={styles.selectList}>
                <Box sx={styles.newProposalHeader}>
                  <Typography variant="body2" sx={styles.textBox}>
                    {messages.currentProposal}
                  </Typography>
                  <Typography variant="body2" sx={styles.textBox}>
                    {messages.newProposal}
                  </Typography>
                </Box>

                <ReplaceBudgetProposalRow
                  mapReplacingProposal={mapReplacingProposal}
                  isLoadingProposals={isLoadingProposals}
                  budget={budget}
                  proposalFieldsDetails={proposalFieldsDetails}
                  replacingproposalList={replacingProposalsList}
                  messages={messages}
                  onChangeReplaceProposalSelection={onChangeReplaceProposalSelection}
                />
              </Box>
            ) : null}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          data-test="cancel-button"
          onClick={onClose}
        >
          {messages.cancel}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={!selectedReplacingProposal.length}
          data-test="replace-button"
          onClick={replaceBudget}
          startIcon={isReplacing ? <CircularProgress size={16} color="inherit" aria-label="Loading" /> : undefined}
        >
          {!selectedBudget ? messages.add : messages.replace}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
